'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useLanguage, LANGUAGES } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';

interface LanguageSwitcherProps {
  compact?: boolean;
}

export default function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { language, setLanguage, currentLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-border bg-white hover:bg-muted transition-colors text-sm font-medium text-foreground"
        aria-label="Select language"
      >
        <span className="text-base leading-none">{currentLang.flag}</span>
        {!compact && (
          <span className="hidden sm:inline text-xs font-semibold text-muted-foreground">
            {currentLang.code.toUpperCase()}
          </span>
        )}
        <Icon name="ChevronDownIcon" size={14} className={`text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 bg-white border border-border rounded-xl shadow-lg z-50 overflow-hidden py-1">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => { setLanguage(lang.code); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                language === lang.code
                  ? 'bg-primary/10 text-primary font-semibold' :'text-foreground hover:bg-muted'
              }`}
            >
              <span className="text-base">{lang.flag}</span>
              <div className="flex flex-col items-start">
                <span className="font-medium leading-tight">{lang.nativeLabel}</span>
                {lang.nativeLabel !== lang.label && (
                  <span className="text-xs text-muted-foreground leading-tight">{lang.label}</span>
                )}
              </div>
              {language === lang.code && (
                <Icon name="CheckIcon" size={14} className="ml-auto text-primary flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
